import { Signer } from 'ethers';

import config from '#/config';
import { waitAsync } from '#/lib/promiseTools';
import { DelegatedKeyAuthorization } from '#/reduxState/delegatedKey/authorizationReducer';
import { IWallet } from '#/reduxState/wallet/walletReducer';

import { clearGlobalEnforcedChainId } from '../chains';
import { getSigner, getUncheckedSigner } from '../wallets';
import { triggerWalletChainSwitchIfPossible } from '../wallets/switchEthereumChain';

// We need to make sure that there are no 2 pending signatures
let activePromiseController: AbortController;

export const stopPendingSignatureRequest = () => {
  if (activePromiseController) {
    activePromiseController.abort();
  }
};

/**
 * This method will make sure there are no 2 parallel sign requests,
 * and also will fail request after period of time (timeout)
 */
export const performWithCancellationAndTimeout = async (
  wallet: IWallet,
  delegatedWallet: DelegatedKeyAuthorization | undefined,
  perform: (signer: Signer) => Promise<string>
): Promise<string> => {
  activePromiseController = new AbortController();

  const enforceChain = !delegatedWallet;

  if (enforceChain) {
    await triggerWalletChainSwitchIfPossible(
      wallet.walletType,
      config.chains.katanaChain,
      await getUncheckedSigner(wallet)
    );
  }

  const signer = await Promise.race([
    getSigner(wallet, delegatedWallet, enforceChain),
    waitAsync(1000 * 60).then(() => {
      throw new Error('Getting wallet timeout');
    }),
  ]);

  if (activePromiseController.signal.aborted) {
    throw new Error('Action canceled');
  }

  return Promise.race([
    perform(signer).then(response => {
      clearGlobalEnforcedChainId();

      if (activePromiseController.signal.aborted) {
        throw new Error('Action canceled');
      }
      return response;
    }),
    waitAsync(1000 * 60 * 2, activePromiseController.signal).then(() => {
      throw new Error('Action timeout');
    }),
  ]);
};

// type TypeValuePair =
//   | ['string' | 'address', string]
//   | ['uint128', string]
//   | ['uint8' | 'uint64', number];

// https://web3js.readthedocs.io/en/v1.2.0/web3-utils.html#soliditysha3
// export const solidityHashOfParams = (params: TypeValuePair[]) => {
//   const fields = params.map(param => param[0]);
//   const values = params.map(param => param[1]);
//   console.debug('Sign:hash', fields, values);
//   return solidityPackedKeccak256(fields, values);
// };

/**
 * When the client gets the signature back from the extension (eg. Metamask),
 * it can simply add 27 (decimal) to the last byte of the signature.
 * Basically reverse this modification https://github.com/MetaMask/eth-ledger-bridge-keyring/blob/3385b3e1d9416fdca6ac92b32224031c223bb909/index.js#L234
 * So if the signature
 *  - ends in 00, change that to 1b
 *  - ends in 01, make it 1c
 *
 * Repro with Ledger Nano S connected through Metamask
 */
export const fixWalletSignature = (signature: string) => {
  // This fix is primarily for Metamask with Ledger
  // but we apply always, because there might be wallets we dont know
  // and use same signature tweak.
  return signature.replace(/00$/, '1b').replace(/01$/, '1c');
};
